// @greenlight-spec: 28-it-dashboard-ui.md#internal-health--internal-health-org-admin
import * as React from 'react';

import type {
  HealthIncident,
  HealthSeries,
  HealthSeriesMetric,
  HealthSeriesServiceKey,
} from '@greenlight/shared';

import { api } from '@/lib/api';

import type { HistoryWindow } from './historyModel';

/** A short range moves every minute; a month does not need a minute-by-minute refresh. */
function pollMsFor(span: HistoryWindow): number {
  return span.step === '1m' || span.step === '5m' ? 60_000 : 300_000;
}

export interface SeriesResult {
  data: HealthSeries | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Showing the previous span's data while this one loads. */
  refreshing: boolean;
}

export function useSeries(
  metric: HealthSeriesMetric,
  span: HistoryWindow,
  service?: HealthSeriesServiceKey,
): SeriesResult {
  const queryKey = ['health-series', metric, span.from, span.to, span.step, service ?? null];
  const query = api.health.series.useQuery(
    queryKey,
    {
      query: {
        metric,
        from: span.from,
        to: span.to,
        step: span.step,
        ...(service ? { service } : {}),
      },
    },
    {
      queryKey,
      refetchInterval: pollMsFor(span),
      placeholderData: (previous) => previous,
    },
  );
  return {
    data: query.data?.status === 200 ? query.data.body : undefined,
    isLoading: query.isLoading,
    isError: query.isError || (query.data !== undefined && query.data.status !== 200),
    refreshing: query.isPlaceholderData,
  };
}

export interface WindowIncidents {
  items: HealthIncident[];
  /** More incidents fell in the window than one page holds. */
  hasMore: boolean;
  isError: boolean;
}

/** Incidents that were live inside the span, for the charts' markers. */
export function useWindowIncidents(span: HistoryWindow): WindowIncidents {
  const queryKey = ['health-incidents-span', span.from, span.to];
  const query = api.health.incidents.useQuery(
    queryKey,
    { query: { from: span.from, to: span.to, limit: 100 } },
    { queryKey, refetchInterval: pollMsFor(span), placeholderData: (previous) => previous },
  );
  const ok = query.data?.status === 200 ? query.data.body : undefined;
  const isError = query.isError || (query.data !== undefined && query.data.status !== 200);
  // Stable across renders, so the markers built from it are not rebuilt each time.
  return React.useMemo(
    () => ({ items: ok?.items ?? [], hasMore: ok?.next_cursor != null, isError }),
    [ok, isError],
  );
}
